import React, { useState, useEffect, useMemo, useRef } from "react";
import ConfirmationModal from './confirmationModal.js';
import ChangePasswordModal from './ChangePasswordModal.js';
import { TextField, MenuItem, Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faKey, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../translations.js";
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { guardWrite } from '../utils/permissions';
import './Administration.css';

const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "(GMT +00:00) UTC" },
  { value: "Europe/London", label: "(GMT +00:00) Europe/London" },
  { value: "Europe/Berlin", label: "(GMT +01:00) Europe/Berlin" },
  { value: "Europe/Paris", label: "(GMT +01:00) Europe/Paris" },
  { value: "Europe/Madrid", label: "(GMT +01:00) Europe/Madrid" },
  { value: "Europe/Rome", label: "(GMT +01:00) Europe/Rome" },
  { value: "Europe/Amsterdam", label: "(GMT +01:00) Europe/Amsterdam" },
  { value: "Europe/Brussels", label: "(GMT +01:00) Europe/Brussels" },
  { value: "Europe/Vienna", label: "(GMT +01:00) Europe/Vienna" },
  { value: "Europe/Zurich", label: "(GMT +01:00) Europe/Zurich" },
  { value: "Europe/Stockholm", label: "(GMT +01:00) Europe/Stockholm" },
  { value: "Europe/Oslo", label: "(GMT +01:00) Europe/Oslo" },
  { value: "Europe/Copenhagen", label: "(GMT +01:00) Europe/Copenhagen" },
  { value: "Europe/Warsaw", label: "(GMT +01:00) Europe/Warsaw" },
  { value: "Europe/Prague", label: "(GMT +01:00) Europe/Prague" },
  { value: "Europe/Budapest", label: "(GMT +01:00) Europe/Budapest" },
  { value: "Europe/Helsinki", label: "(GMT +02:00) Europe/Helsinki" },
  { value: "Europe/Bucharest", label: "(GMT +02:00) Europe/Bucharest" },
  { value: "Europe/Athens", label: "(GMT +02:00) Europe/Athens" },
  { value: "Europe/Istanbul", label: "(GMT +03:00) Europe/Istanbul" },
  { value: "Europe/Moscow", label: "(GMT +03:00) Europe/Moscow" },
  { value: "US/Eastern", label: "(GMT -05:00) US/Eastern" },
  { value: "US/Central", label: "(GMT -06:00) US/Central" },
  { value: "US/Mountain", label: "(GMT -07:00) US/Mountain" },
  { value: "US/Pacific", label: "(GMT -08:00) US/Pacific" },
  { value: "US/Alaska", label: "(GMT -09:00) US/Alaska" },
  { value: "US/Hawaii", label: "(GMT -10:00) US/Hawaii" },
  { value: "Canada/Atlantic", label: "(GMT -04:00) Canada/Atlantic" },
  { value: "Canada/Eastern", label: "(GMT -05:00) Canada/Eastern" },
  { value: "Canada/Central", label: "(GMT -06:00) Canada/Central" },
  { value: "Canada/Mountain", label: "(GMT -07:00) Canada/Mountain" },
  { value: "Canada/Pacific", label: "(GMT -08:00) Canada/Pacific" },
  { value: "America/Mexico_City", label: "(GMT -06:00) America/Mexico City" },
  { value: "America/Bogota", label: "(GMT -05:00) America/Bogota" },
  { value: "America/Lima", label: "(GMT -05:00) America/Lima" },
  { value: "America/Santiago", label: "(GMT -04:00) America/Santiago" },
  { value: "America/Buenos_Aires", label: "(GMT -03:00) America/Buenos Aires" },
  { value: "America/Sao_Paulo", label: "(GMT -03:00) America/Sao Paulo" },
  { value: "Asia/Dubai", label: "(GMT +04:00) Asia/Dubai" },
  { value: "Asia/Riyadh", label: "(GMT +03:00) Asia/Riyadh" },
  { value: "Asia/Kolkata", label: "(GMT +05:30) Asia/Kolkata" },
  { value: "Asia/Bangkok", label: "(GMT +07:00) Asia/Bangkok" },
  { value: "Asia/Singapore", label: "(GMT +08:00) Asia/Singapore" },
  { value: "Asia/Hong_Kong", label: "(GMT +08:00) Asia/Hong Kong" },
  { value: "Asia/Shanghai", label: "(GMT +08:00) Asia/Shanghai" },
  { value: "Asia/Seoul", label: "(GMT +09:00) Asia/Seoul" },
  { value: "Asia/Tokyo", label: "(GMT +09:00) Asia/Tokyo" },
  { value: "Australia/Sydney", label: "(GMT +11:00) Australia/Sydney" },
  { value: "Australia/Melbourne", label: "(GMT +11:00) Australia/Melbourne" },
  { value: "Australia/Perth", label: "(GMT +08:00) Australia/Perth" },
  { value: "Pacific/Auckland", label: "(GMT +13:00) Pacific/Auckland" },
  { value: "Africa/Cairo", label: "(GMT +02:00) Africa/Cairo" },
  { value: "Africa/Lagos", label: "(GMT +01:00) Africa/Lagos" },
  { value: "Africa/Johannesburg", label: "(GMT +02:00) Africa/Johannesburg" },
  { value: "Africa/Nairobi", label: "(GMT +03:00) Africa/Nairobi" },
];

const formatTimeInTz = (tz, date) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  } catch {
    return "";
  }
};

const Administration = ({ logoutUser, onSave }) => {
  const { t } = useTranslation();
  const { registerUnsavedChangesGuard } = useUnsavedChanges();
  const [settings, setSettings] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    defaultLanguage: "en",
    dateFormat: "YYYY-MM-DD",
    timezone: "UTC",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [initialSettings, setInitialSettings] = useState(null);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Tick once a minute so the live time shown next to each timezone option
  // (and in the selected timezone display) stays accurate.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const timezoneOptions = useMemo(
    () =>
      TIMEZONE_OPTIONS.map((tz) => ({
        ...tz,
        time: formatTimeInTz(tz.value, now),
      })),
    [now]
  );

  const renderTimeBadge = (time, inverted = false) => (
    <span
      style={{
        display: 'inline-block',
        marginLeft: 8,
        padding: '2px 8px',
        borderRadius: 12,
        backgroundColor: inverted ? '#ffffff' : '#5616ea',
        color: inverted ? '#5616ea' : '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {time}
    </span>
  );

  useEffect(() => {
    const userId = localStorage.getItem("user_id") || "";
    if (userId) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_BASE_URL}users/${userId}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Error: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          const initial = {
            firstName: data.first_name || "",
            lastName: data.last_name || "",
            email: data.email || "",
            password: "",
            defaultLanguage: data.language || "en",
            dateFormat: data.date_format || "YYYY-MM-DD",
            timezone: data.timezone || "UTC",
          };
          setSettings(initial);
          setInitialSettings(initial);
          setHasLoadedInitial(true);
        })
        .catch((err) => {
          console.error("Failed to fetch user data:", err);
          setError(t("error_fetching_user_data"));
        })
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedInitial || !initialSettings) {
      return;
    }
    const isChanged = JSON.stringify(initialSettings) !== JSON.stringify(settings);
    setHasUnsavedChanges(isChanged);
  }, [settings, initialSettings, hasLoadedInitial]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSettings({ ...settings, [name]: value });
  };

  const handleSave = async () => {
    // Read-only teammates: revert local edits and surface the shared
    // permission modal instead of attempting the PUT (which would 403).
    if (!guardWrite(() => handleDiscardChanges())) return;
    const userId = localStorage.getItem("user_id") || "";
    if (!userId) {
      setError(t("user_id_not_found"));
      return t("user_id_not_found");
    }

    // Validate first name is not empty
    if (!settings.firstName || settings.firstName.trim() === "") {
      setError(t("first_name_required"));
      return t("first_name_required");
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settings.email)) {
      setError(t("invalid_email_format"));
      return t("invalid_email_format");
    }

    const updatePayload = {
      first_name: settings.firstName.trim(),
      last_name: settings.lastName.trim(),
      email: settings.email,
      language: settings.defaultLanguage,
      date_format: settings.dateFormat,
      timezone: settings.timezone,
    };

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409 && errorData.detail === "email_already_in_use") {
          setError(t("email_already_in_use"));
          return t("email_already_in_use");
        }
        throw new Error(`Error: ${response.status}`);
      }

      await response.json();

      setInitialSettings(settings);
      setHasUnsavedChanges(false);

      onSave(settings.defaultLanguage, settings.dateFormat, settings.timezone);
      return true;
    } catch (err) {
      console.error("Failed to update user:", err);
      setError(t("error_updating_user"));
      return t("error_updating_user");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  };

  // Keep latest handlers in a ref so guard registration doesn't re-run on every keystroke.
  const unsavedHandlersRef = useRef({ save: handleSave, discard: handleDiscardChanges });
  unsavedHandlersRef.current = { save: handleSave, discard: handleDiscardChanges };

  useEffect(() => {
    return registerUnsavedChangesGuard('administration-settings', {
      when: hasUnsavedChanges,
      onSave: (...args) => unsavedHandlersRef.current.save(...args),
      onDiscard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
  }, [hasUnsavedChanges, registerUnsavedChangesGuard]);

  const handleDeleteClick = () => {
    setOpenModal(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    const userId = localStorage.getItem("user_id") || "";

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete user. Status: ${response.status}`);
      }

    } catch (err) {
      console.error('Error deleting user:', err);
    } finally {
      setLoading(false);
      setOpenModal(false);
      logoutUser();
    }
  };

  if (loading && !hasLoadedInitial) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="user-settings-container">
        {error && (
          <div className="adm-error-message">
            <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
            <span>{error}</span>
          </div>
        )}
        <Box className="adm-control-group">
          <TextField
            label={t("first_name")}
            name="firstName"
            value={settings.firstName}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            className="user-settings-input"
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                "& fieldset": {
                  border: "1px solid #515151",
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
              },
            }}
          />
          <TextField
            label={t("last_name")}
            name="lastName"
            value={settings.lastName}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            className="user-settings-input"
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                "& fieldset": {
                  border: "1px solid #515151",
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
              },
            }}
          />
          <TextField
            label={t("email")}
            name="email"
            type="email"
            value={settings.email}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            autoComplete="email"
            className="user-settings-input"
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                "& fieldset": {
                  border: "1px solid #515151",
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
              },
            }}
          />
          <TextField
            select
            label={t("language")}
            name="defaultLanguage"
            value={settings.defaultLanguage}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            className="user-settings-input"
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                "& fieldset": {
                  border: "1px solid #515151",
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
              },
            }}
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <MenuItem
                key={lng.code}
                value={lng.code}
                sx={{
                  backgroundColor: '#ffffff !important',
                  '&:hover': {
                    backgroundColor: '#bbe4f6 !important',
                    color: 'black !important',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#5616ea !important',
                    color: 'white !important',
                  },
                }}
              >
                {lng.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t("date_format")}
            name="dateFormat"
            value={settings.dateFormat}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            className="user-settings-input"
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                "& fieldset": {
                  border: "1px solid #515151",
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
              },
            }}
          >
            <MenuItem
              value="YYYY-MM-DD"
              sx={{
                backgroundColor: settings.dateFormat === 'YYYY-MM-DD' ? '#ffffff !important' : '#ffffff !important',
                '&:hover': {
                  backgroundColor: '#bbe4f6 !important',
                  color: 'black !important',
                },
                '&.Mui-selected': {
                  backgroundColor: '#5616ea !important',
                  color: 'white !important',
                },
              }}
            >
              YYYY-MM-DD
            </MenuItem>
            <MenuItem
              value="DD-MM-YYYY"
              sx={{
                backgroundColor: settings.dateFormat === 'DD-MM-YYYY' ? '#ffffff !important' : '#ffffff !important',
                '&:hover': {
                  backgroundColor: '#bbe4f6 !important',
                  color: 'black !important',
                },
                '&.Mui-selected': {
                  backgroundColor: '#5616ea !important',
                  color: 'white !important',
                },
              }}
            >
              DD-MM-YYYY
            </MenuItem>
            <MenuItem
              value="MM-DD-YYYY"
              sx={{
                backgroundColor: settings.dateFormat === 'MM-DD-YYYY' ? '#ffffff !important' : '#ffffff !important',
                '&:hover': {
                  backgroundColor: '#bbe4f6 !important',
                  color: 'black !important',
                },
                '&.Mui-selected': {
                  backgroundColor: '#5616ea !important',
                  color: 'white !important',
                },
              }}
            >
              MM-DD-YYYY
            </MenuItem>
          </TextField>
          <TextField
            select
            label={t("timezone")}
            name="timezone"
            value={settings.timezone}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            className="user-settings-input"
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused": {
                "& fieldset": {
                  border: "1px solid #515151",
                },
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': {
                  color: '#5616ea',
                },
              },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 300 },
                },
              },
              renderValue: (selected) => {
                const tz = timezoneOptions.find((o) => o.value === selected);
                if (!tz) return selected;
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {tz.label}
                    {tz.time && renderTimeBadge(tz.time)}
                  </span>
                );
              },
            }}
          >
            {timezoneOptions.map((tz) => (
              <MenuItem
                key={tz.value}
                value={tz.value}
                sx={{
                  backgroundColor: '#ffffff !important',
                  '&:hover': {
                    backgroundColor: '#bbe4f6 !important',
                    color: 'black !important',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#5616ea !important',
                    color: 'white !important',
                  },
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {tz.label}
                  {tz.time && renderTimeBadge(tz.time, settings.timezone === tz.value)}
                </span>
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {hasUnsavedChanges && (
          <div className="unsaved-changes-message">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">{t("unsaved_changes_info")}</div>
          </div>
        )}
        <div className="adm-actions-row">
          <button
            className="adm-pwd-btn"
            onClick={() => setOpenPasswordModal(true)}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faKey} /> {t('change_password')}
          </button>
          <div className="adm-actions-right">
            {hasUnsavedChanges && (
              <button
                onClick={handleDiscardChanges}
                className="adm-discard-btn"
                disabled={loading}
              >
                {t("discard_changes")}
              </button>
            )}
            <button
              className="adm-save-btn"
              onClick={handleSave}
              disabled={loading || !hasUnsavedChanges}
            >
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : t('save')}
            </button>
          </div>
          {/* <Button
              onClick={handleDeleteClick}
              variant="contained"
              className="remove-account-btn"
              sx={{
              backgroundColor: '#5616ea',
              '&:hover': {
                backgroundColor: '#4a14d6',
              },
            }}
            >
            {t('remove_account')}
          </Button> */}
        </div>
      </div>
      <ConfirmationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName={settings.firstName}
        requireTextConfirmation={true}
      />
      <ChangePasswordModal
        open={openPasswordModal}
        onClose={() => setOpenPasswordModal(false)}
        userEmail={settings.email}
      />
    </div>
  );
};

export default Administration;
